import React, { useState } from 'react';

// Fixed pixel widths for the "identity" columns (Campaign..Metric). Scroll with the
// rest of the table horizontally rather than staying pinned to the left — pinning
// them put them visually on top of the question columns while side-scrolling, which
// read as broken/cramped rather than helpful.
const BASE_COLS = [
  { key: 'campaign', label: 'Campaign', width: 140 },
  { key: 'scorecard', label: 'Scorecard', width: 160 },
  { key: 'agent', label: 'Agent', width: 140 },
  { key: 'guid', label: 'Guid', width: 220 },
  { key: 'conversationtime', label: 'conversationtime', width: 140 },
  { key: 'qaScore', label: 'QA Score', width: 90 },
  { key: 'metric', label: 'Metric', width: 190 },
];

const QUESTION_COL_WIDTH = 260;

// Status reads Pass, Fail, Violation or N/A (pca-exec-summary's questionStatusLabel).
// success/failed are the raw statuses a report generated before that still carries.
export function statusCellClass(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'pass' || v === 'success') return 'bg-green-50 text-green-800 font-semibold';
  if (v === 'fail' || v === 'violation' || v === 'failed') return 'bg-red-50 text-red-800 font-semibold';
  if (v === 'n/a') return 'text-gray-500';
  return '';
}

// A score-tinted left rail per call block gives a quick visual read of pass/
// fail before reading a single cell, and doubles as the divider between one
// call's block of metric rows and the next.
function scoreRailClass(qaScore) {
  const score = Number(qaScore);
  if (Number.isNaN(score)) return 'border-l-4 border-l-gray-200';
  if (score >= 90) return 'border-l-4 border-l-green-400';
  if (score < 60) return 'border-l-4 border-l-red-400';
  return 'border-l-4 border-l-amber-400';
}

export const SpreadsheetGrid = ({ columns, calls }) => {
  // Which question header is currently hovered, so its tooltip can render — plain
  // React state instead of a CSS :hover/group-hover variant, since inline styles are
  // the one styling mechanism guaranteed to apply regardless of the Tailwind build.
  const [hoveredQuestion, setHoveredQuestion] = useState(null);

  if (!calls || calls.length === 0) {
    return (
      <div className="card p-8 text-center text-gray-500">
        No records to display for the selected filters.
      </div>
    );
  }

  return (
    <div className="card !p-0 overflow-hidden">
      <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
        <table className="border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              {BASE_COLS.map((col) => (
                <th
                  key={col.key}
                  className="sticky top-0 z-20 bg-[#fafafc] text-gray-500 text-left text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5 border border-gray-200"
                  style={{ width: col.width, maxWidth: col.width, overflow: 'hidden' }}
                >
                  {col.label}
                </th>
              ))}
              {columns.map((q) => (
                <th
                  key={q}
                  className="sticky top-0 z-10 bg-[#fafafc] text-gray-500 text-left text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5 border border-gray-200"
                  // maxWidth, not minWidth: table-layout:fixed still lets an unbreakable
                  // nowrap string (a long question with no natural break point) push a
                  // column past a plain width/minWidth pair in some browsers, silently
                  // defeating overflow:hidden. max-width is a hard ceiling regardless.
                  // No overflow:hidden here (unlike the other cells) — the tooltip below
                  // is an absolutely-positioned child that needs to render outside this
                  // cell's box; overflow:hidden on the cell itself would clip it. The
                  // inner span's own overflow:hidden still handles the text ellipsis.
                  style={{ width: QUESTION_COL_WIDTH, maxWidth: QUESTION_COL_WIDTH }}
                  onMouseEnter={() => setHoveredQuestion(q)}
                  onMouseLeave={() => setHoveredQuestion((prev) => (prev === q ? null : prev))}
                >
                  {/* Truncated to keep a long question from wrapping the header row
                      across multiple lines and pushing the whole grid down — the full
                      text is still there on hover. All inline styles rather than
                      Tailwind's truncate/group-hover utilities, so it doesn't depend on
                      those being present in the compiled CSS. Doesn't touch the data
                      itself, so the XLSX/CSV exports (built server-side from the same
                      columns) always get the untruncated question. */}
                  <div style={{ position: 'relative' }}>
                    <span
                      aria-label={q}
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'default',
                      }}
                    >
                      {q}
                    </span>
                    {hoveredQuestion === q && (
                      <div
                        role="tooltip"
                        style={{
                          display: 'block',
                          position: 'absolute',
                          left: 0,
                          top: '100%',
                          zIndex: 30,
                          marginTop: 4,
                          width: 'max-content',
                          maxWidth: 320,
                          borderRadius: 4,
                          backgroundColor: '#1f2937',
                          padding: '6px 8px',
                          fontSize: 12,
                          fontWeight: 400,
                          color: '#ffffff',
                          textTransform: 'none',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
                          whiteSpace: 'normal',
                        }}
                      >
                        {q}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calls.map((call, callIdx) => {
              const blockShade = callIdx % 2 === 1 ? 'bg-cloudza-50' : 'bg-white';
              const rail = scoreRailClass(call.qaScore);
              return call.rows.map((row, rowIdx) => {
                const isFirst = rowIdx === 0;
                return (
                  <tr key={`${call.guid}-${rowIdx}`} className={blockShade}>
                    {isFirst &&
                      BASE_COLS.slice(0, 6).map((col, i) => (
                        <td
                          key={col.key}
                          rowSpan={call.rows.length}
                          className={`align-middle px-3 py-1.5 border border-gray-200 ${blockShade} ${i === 0 ? rail : ''} truncate`}
                          style={{ width: col.width, maxWidth: col.width, overflow: 'hidden' }}
                          title={call[col.key]}
                        >
                          {call[col.key]}
                        </td>
                      ))}
                    <td
                      className={`px-3 py-1.5 border border-gray-200 font-semibold text-gray-500 text-[11px] uppercase tracking-wider ${blockShade}`}
                      style={{ width: BASE_COLS[6].width, maxWidth: BASE_COLS[6].width, overflow: 'hidden' }}
                    >
                      {row.label}
                    </td>
                    {columns.map((q) => (
                      <td
                        key={q}
                        className={`px-3 py-1.5 border border-gray-200 align-top ${row.label === 'Status' ? statusCellClass(row.values[q]) : ''}`}
                        style={{ width: QUESTION_COL_WIDTH, maxWidth: QUESTION_COL_WIDTH, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}
                      >
                        {row.values[q]}
                      </td>
                    ))}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SpreadsheetGrid;
